import { authedFetch } from '../../../lib/authedFetch';
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { Button } from '../../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { AreaChart, Area, LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { TrendingUp, Database, Target, Brain, Download, Clock, RefreshCw, CheckCircle, AlertTriangle, MessageSquare, Zap } from 'lucide-react';
import { useBusiness } from '../../../contexts/BusinessContext';
import { useLanguage } from '../../../contexts/LanguageContext';
import ExportMenu from '../../Common/ExportMenu';
import { buildResolutionRateTrendModel, buildAnalystIntentPercentageModel, buildSolutionStatsChartModel } from '../../../lib/chartModels';

const AnalyticsDashboard = () => {
  const { currentBusiness } = useBusiness();
  const { t } = useLanguage();
  const [timeRange, setTimeRange] = useState('30');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [businessMetrics, setBusinessMetrics] = useState(null);
  const [chatMetrics, setChatMetrics] = useState(null);
  const [solutionStats, setSolutionStats] = useState({ total: 0, approved: 0, pending: 0 });

  const backendUrl = process.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_API_BASE || '';

  const fetchAll = useCallback(async () => {
    const businessId = currentBusiness?.brandId;
    if (!businessId) return;
    setLoading(true);
    setError(null);
    try {
            const [bizRes, chatRes, solTotalRes, solApprovedRes, solPendingRes] = await Promise.allSettled([
        authedFetch(`${backendUrl}/api/v1/analytics/business/${businessId}?period=${timeRange}d`),
        authedFetch(`${backendUrl}/api/v1/ai-chat/analytics/chat-metrics?business_id=${businessId}`),
        authedFetch(`${backendUrl}/api/v1/solutions?business_id=${businessId}&limit=1`),
        authedFetch(`${backendUrl}/api/v1/solutions?business_id=${businessId}&status=approved&limit=1`),
        authedFetch(`${backendUrl}/api/v1/solutions?business_id=${businessId}&status=pending_approval&limit=1`),
      ]);

      if (bizRes.status === 'fulfilled' && bizRes.value.ok) {
        setBusinessMetrics(await bizRes.value.json());
      }
      if (chatRes.status === 'fulfilled' && chatRes.value.ok) {
        setChatMetrics(await chatRes.value.json());
      }

      const totals = { total: 0, approved: 0, pending: 0 };
      if (solTotalRes.status === 'fulfilled' && solTotalRes.value.ok) {
        const d = await solTotalRes.value.json();
        totals.total = d.total || 0;
      }
      if (solApprovedRes.status === 'fulfilled' && solApprovedRes.value.ok) {
        const d = await solApprovedRes.value.json();
        totals.approved = d.total || 0;
      }
      if (solPendingRes.status === 'fulfilled' && solPendingRes.value.ok) {
        const d = await solPendingRes.value.json();
        totals.pending = d.total || 0;
      }
      setSolutionStats(totals);

    } catch (err) {
      console.error('AnalyticsDashboard fetch error:', err);
      setError(t('rd.errLoadAnalytics'));
    } finally {
      setLoading(false);
    }
  }, [currentBusiness?.brandId, timeRange, backendUrl]);

  useEffect(() => {
    if (currentBusiness?.brandId) fetchAll();
  }, [currentBusiness?.brandId, timeRange]);

  const handleExportData = () => {
    const data = { businessMetrics, chatMetrics, solutionStats, exportedAt: new Date().toISOString() };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `analytics-export-${Date.now()}.json`;
    a.click();
  };

  // Derived values
  const aiResolutionRate = chatMetrics ? chatMetrics.auto_resolution_rate : null;
  const escalationRate = chatMetrics ? chatMetrics.escalation_rate : null;
  const totalTickets = businessMetrics ? businessMetrics.total_tickets : null;
  const avgResolution = businessMetrics ? businessMetrics.avg_resolution_time : null;
  const avgFirstResponse = businessMetrics ? businessMetrics.avg_first_response_time : null;
  const avgChatDuration = businessMetrics ? businessMetrics.avg_chat_duration : null;
  const openaiUsageRate = businessMetrics ? businessMetrics.openai_usage_rate : null;
  const reopenedConvs = businessMetrics ? businessMetrics.reopened_conversations : null;
  const callbackRate = businessMetrics ? businessMetrics.callback_rate : null;

  // Resolution rate trend from daily_metrics
  const resolutionTrendData = buildResolutionRateTrendModel(businessMetrics?.daily_metrics);

  // Intent distribution from chat metrics (pure model in lib/chartModels, unit-tested).
  const intentData = buildAnalystIntentPercentageModel(chatMetrics?.intent_distribution, chatMetrics?.total_conversations);

  // Language distribution
  const languageData = Object.entries(chatMetrics?.language_distribution || {})
    .map(([name, count]) => ({ name: name.charAt(0).toUpperCase() + name.slice(1), count }));

  // Projected volume for predictions (extrapolate from period data)
  const daysInPeriod = parseInt(timeRange, 10) || 30;
  const projectedMonthlyTickets = totalTickets != null
    ? Math.round((totalTickets / daysInPeriod) * 30)
    : null;
  const projectedResolutionRate = aiResolutionRate != null
    ? Math.min(99, Math.round(aiResolutionRate * 1.05))
    : null;

  if (loading && !businessMetrics && !chatMetrics) {
    return (
      <div className="flex-1 h-full flex items-center justify-center bg-gradient-to-br from-white via-green-50 to-purple-50">
        <div className="text-center">
          <RefreshCw className="animate-spin rounded-full h-12 w-12 text-green-600 mx-auto mb-4" />
          <p className="text-gray-600">{t('rd.loadingAnalytics')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 h-full overflow-y-auto p-6 bg-gradient-to-br from-white via-green-50 to-purple-50">
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('nav.aiPerformance')}</h1>
          <p className="text-gray-600">{t('rd.aiPerfSubtitle')}</p>
        </div>
        <div className="flex gap-3">
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">{t('rd.days7')}</SelectItem>
              <SelectItem value="30">{t('rd.days30')}</SelectItem>
              <SelectItem value="90">{t('rd.days90')}</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={fetchAll} variant="outline" disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            {t('common.refresh')}
          </Button>
          <Button onClick={handleExportData} variant="outline">
            <Download className="h-4 w-4 mr-2" />
            {t('rd.exportData')}
          </Button>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 text-red-600 text-sm rounded border border-red-200">{error}</div>
      )}

      {/* Key Metrics Cards */}
      <div className="grid md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{t('rd.aiResolutionRate')}</p>
                <p className="text-3xl font-bold text-green-600">
                  {aiResolutionRate != null ? `${aiResolutionRate}%` : '—'}
                </p>
                <p className="text-xs text-gray-500">{t('rd.chatAutoResolved')}</p>
              </div>
              <Target className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{t('rd.avgResolutionTime')}</p>
                <p className="text-3xl font-bold text-blue-600">
                  {avgResolution != null ? `${avgResolution}h` : '—'}
                </p>
                <p className="text-xs text-gray-500">{t('rd.ticketCreateClose')}</p>
              </div>
              <Clock className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{t('rd.totalTickets')}</p>
                <p className="text-3xl font-bold text-orange-600">
                  {totalTickets != null ? totalTickets.toLocaleString() : '—'}
                </p>
                <p className="text-xs text-gray-500">{t('rd.escalation')}: {escalationRate != null ? `${escalationRate}%` : '—'}</p>
              </div>
              <TrendingUp className="h-8 w-8 text-orange-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{t('rd.openaiUsageRate')}</p>
                <p className="text-3xl font-bold text-purple-600">
                  {openaiUsageRate != null ? `${openaiUsageRate}%` : '—'}
                </p>
                <p className="text-xs text-gray-500">{t('rd.aiFallbackGpt')}</p>
              </div>
              <Zap className="h-8 w-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Secondary Metrics Row */}
      <div className="grid md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-gray-500">{t('rd.avgFirstResponse')}</p>
            <p className="text-2xl font-bold text-teal-600">
              {avgFirstResponse != null ? `${avgFirstResponse}m` : '—'}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-gray-500">{t('rd.avgChatDuration')}</p>
            <p className="text-2xl font-bold text-indigo-600">
              {avgChatDuration != null ? `${avgChatDuration}m` : '—'}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-gray-500">{t('rd.callbackRate')}</p>
            <p className="text-2xl font-bold text-yellow-600">
              {callbackRate != null ? `${callbackRate}%` : '—'}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-gray-500">{t('rd.reopenedConversations')}</p>
            <p className={`text-2xl font-bold ${(reopenedConvs || 0) > 0 ? 'text-red-600' : 'text-green-600'}`}>
              {reopenedConvs != null ? reopenedConvs : '—'}
            </p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="resolution_trend" className="space-y-6">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="resolution_trend">{t('rd.resolutionTrend')}</TabsTrigger>
          <TabsTrigger value="knowledge">{t('nav.solutionBank')}</TabsTrigger>
          <TabsTrigger value="intents">{t('rd.intentCoverage')}</TabsTrigger>
          <TabsTrigger value="predictions">{t('rd.forecast')}</TabsTrigger>
        </TabsList>

        {/* Resolution Rate Trend */}
        <TabsContent value="resolution_trend" className="space-y-6">
          <Card id="resolution-trend-chart">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>{t('rd.resolutionEscalationByDay')}</CardTitle>
              <ExportMenu
                data={resolutionTrendData}
                element="#resolution-trend-chart"
                filename="resolution-trend"
                title={t('rd.resolutionRateTrend')}
              />
            </CardHeader>
            <CardContent>
              {resolutionTrendData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={resolutionTrendData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis unit="%" domain={[0, 100]} />
                    <Tooltip formatter={(val) => `${val}%`} />
                    <Legend />
                    <Area type="monotone" dataKey="resolutionRate" stroke="#4CAF50" fill="#E8F5E9" name={t('rd.resolutionPct')} />
                    <Area type="monotone" dataKey="escalationRate" stroke="#F44336" fill="#FFEBEE" name={t('rd.escalationPct')} />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-64 flex items-center justify-center text-gray-400">
                  {t('rd.noTicketData')}
                </div>
              )}
            </CardContent>
          </Card>

          <div className="grid md:grid-cols-3 gap-4">
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-gray-500">{t('rd.chatConversations')}</p>
                <p className="text-2xl font-bold">{chatMetrics?.total_conversations ?? '—'}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-gray-500">{t('rd.autoResolved')}</p>
                <p className="text-2xl font-bold text-green-600">{chatMetrics?.auto_resolved_count ?? '—'}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-gray-500">{t('rd.avgAiConfidence')}</p>
                <p className="text-2xl font-bold text-blue-600">
                  {chatMetrics?.average_confidence != null ? `${Math.round(chatMetrics.average_confidence * 100)}%` : '—'}
                </p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Solution Bank */}
        <TabsContent value="knowledge" className="space-y-6">
          <Card id="solution-bank-chart">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>{t('rd.solutionBankStatus')}</CardTitle>
              <ExportMenu
                data={[solutionStats]}
                element="#solution-bank-chart"
                filename="solution-bank-stats"
                title={t('rd.solutionBankStats')}
              />
            </CardHeader>
            <CardContent>
              {solutionStats.total > 0 ? (
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={buildSolutionStatsChartModel(solutionStats, { total: t('rd.total'), approved: t('rd.approved'), pending: t('rd.pending') })}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="count" fill="#4A90D9" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-32 flex items-center justify-center text-gray-400">
                  {t('rd.noSolutionBankData')}
                </div>
              )}
            </CardContent>
          </Card>
          <div className="grid md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-gray-500">{t('rd.totalSolutions')}</p>
                <p className="text-2xl font-bold">{solutionStats.total.toLocaleString()}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-gray-500">{t('rd.approved')}</p>
                <p className="text-2xl font-bold text-green-600">{solutionStats.approved.toLocaleString()}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-gray-500">{t('nav.pendingReview')}</p>
                <p className="text-2xl font-bold text-yellow-600">{solutionStats.pending.toLocaleString()}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-gray-500">{t('rd.approvalRate')}</p>
                <p className="text-2xl font-bold text-blue-600">
                  {solutionStats.total > 0
                    ? `${Math.round((solutionStats.approved / solutionStats.total) * 100)}%`
                    : '—'}
                </p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Intent Coverage */}
        <TabsContent value="intents" className="space-y-6">
          <Card id="intent-coverage-chart">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>{t('rd.intentDistributionChat')}</CardTitle>
              <ExportMenu
                data={intentData}
                element="#intent-coverage-chart"
                filename="intent-distribution"
                title={t('rd.intentDistribution')}
              />
            </CardHeader>
            <CardContent>
              {intentData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={intentData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" angle={-30} textAnchor="end" height={60} />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="count" fill="#8884d8" name={t('rd.conversations')} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-64 flex items-center justify-center text-gray-400">
                  {t('rd.noIntentData')}
                </div>
              )}
            </CardContent>
          </Card>

          {languageData.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>{t('rd.languageDistribution')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {languageData.map((item, i) => {
                    const pct = chatMetrics?.total_conversations > 0
                      ? Math.round((item.count / chatMetrics.total_conversations) * 100)
                      : 0;
                    return (
                      <div key={i}>
                        <div className="flex justify-between mb-1">
                          <span className="text-sm font-medium">{item.name}</span>
                          <span className="text-sm text-gray-500">{item.count} ({pct}%)</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-blue-500 h-2 rounded-full"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Forecast */}
        <TabsContent value="predictions" className="space-y-6" id="predictions-section">
          <div className="flex justify-end mb-4">
            <ExportMenu
              element="#predictions-section"
              filename="predictions-forecast"
              title={t('rd.aiPredictionsForecasts')}
            />
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Brain className="h-5 w-5" />
                  {t('rd.aiResolutionForecast')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between items-center">
                  <span>{t('rd.currentResolutionRate')}</span>
                  <span className="text-green-600 font-bold">
                    {aiResolutionRate != null ? `${aiResolutionRate}%` : '—'}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span>{t('rd.projectedRate5')}</span>
                  <span className="text-green-600 font-bold">
                    {projectedResolutionRate != null ? `${projectedResolutionRate}%` : '—'}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span>{t('rd.avgAiConfidence')}</span>
                  <span className="text-blue-600 font-bold">
                    {chatMetrics?.average_confidence != null
                      ? `${Math.round(chatMetrics.average_confidence * 100)}%`
                      : '—'}
                  </span>
                </div>
                <div className="mt-4 p-3 bg-green-50 rounded">
                  <p className="text-sm font-medium text-green-800">
                    {aiResolutionRate != null
                      ? aiResolutionRate >= 70 ? t('rd.trendPerformingWell') : t('rd.trendNeedsImprovement')
                      : t('rd.noDataYet')}
                  </p>
                  <p className="text-xs text-green-600">{t('rd.basedOnLiveData', { days: timeRange })}</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>{t('rd.ticketVolumeForecast')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between items-center">
                  <span>{t('rd.currentDayVolume', { days: timeRange })}</span>
                  <span className="text-blue-600 font-bold">
                    {totalTickets != null ? totalTickets.toLocaleString() : '—'}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span>{t('rd.projectedMonthly')}</span>
                  <span className="text-blue-600 font-bold">
                    {projectedMonthlyTickets != null ? projectedMonthlyTickets.toLocaleString() : '—'}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span>{t('rd.currentEscalationRate')}</span>
                  <span className={`font-bold ${escalationRate > 30 ? 'text-red-600' : 'text-orange-600'}`}>
                    {escalationRate != null ? `${escalationRate}%` : '—'}
                  </span>
                </div>
                <div className="mt-4 p-3 bg-blue-50 rounded">
                  <p className="text-sm font-medium text-blue-800">
                    {totalTickets != null ? t('rd.basedOnLiveTicketData') : t('rd.noTicketDataYet')}
                  </p>
                  <p className="text-xs text-blue-600">{t('rd.projectedFromPeriodAvg', { days: timeRange })}</p>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>{t('rd.operationalInsights')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {solutionStats.pending > 0 && (
                <div className="border-l-4 border-l-yellow-500 pl-4">
                  <h3 className="font-bold">{t('rd.solutionBankReviewNeeded')}</h3>
                  <p className="text-sm text-gray-600">
                    {t('rd.pendingApprovalMsg', { count: solutionStats.pending })}
                  </p>
                </div>
              )}
              {(reopenedConvs || 0) > 0 && (
                <div className="border-l-4 border-l-red-500 pl-4">
                  <h3 className="font-bold">{t('rd.reopenedConversations')}</h3>
                  <p className="text-sm text-gray-600">
                    {t('rd.reopenedMsg', { count: reopenedConvs })}
                  </p>
                </div>
              )}
              {(callbackRate || 0) > 20 && (
                <div className="border-l-4 border-l-orange-500 pl-4">
                  <h3 className="font-bold">{t('rd.highCallbackQueue')}</h3>
                  <p className="text-sm text-gray-600">
                    {callbackRate}% {t('rd.callbackRateMsg')}
                  </p>
                </div>
              )}
              {aiResolutionRate != null && aiResolutionRate < 50 && (
                <div className="border-l-4 border-l-purple-500 pl-4">
                  <h3 className="font-bold">{t('rd.expandSolutionBankCoverage')}</h3>
                  <p className="text-sm text-gray-600">
                    {t('rd.aiResolutionLowMsg', { rate: aiResolutionRate })}
                  </p>
                </div>
              )}
              {aiResolutionRate != null && aiResolutionRate >= 70 && (
                <div className="border-l-4 border-l-green-500 pl-4">
                  <h3 className="font-bold">{t('rd.strongAiPerformance')}</h3>
                  <p className="text-sm text-gray-600">
                    {t('rd.aiResolvingMsg', { rate: aiResolutionRate })}
                  </p>
                </div>
              )}
              {!businessMetrics && !chatMetrics && (
                <p className="text-gray-400 text-sm text-center py-4">
                  {t('rd.loadAnalyticsToSeeInsights')}
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AnalyticsDashboard;
